/**
 * Компактный markdown-рендерер под постмиты (и любые AI-документы в чате).
 *
 * Зачем свой, а не react-markdown: это редкий блок в чат-бабле, а библиотека с
 * remark/unified тянет ~45КБ в PWA-бандл мессенджера. Разметку генерит наш же
 * промпт, поэтому подмножество известно и стабильно: # заголовки, **жирный**,
 * `код`, - и 1. списки, | таблицы |, --- разделители, абзацы.
 *
 * Безопасность: рендерим ТОЛЬКО React-узлы, никакого dangerouslySetInnerHTML —
 * разметка из ответа модели не может стать HTML.
 */
import { useMemo, useState, type ReactNode } from "react";

const LONG_MD_THRESHOLD = 500;

/** Инлайн: **жирный** и `код`. */
function inline(s: string, kp: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|`([^`]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push(s.slice(last, m.index));
    if (m[1] !== undefined) out.push(<strong key={`${kp}-b${i++}`}>{m[1]}</strong>);
    else out.push(<code key={`${kp}-c${i++}`} className="md-code">{m[2]}</code>);
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

const isUl = (l: string) => /^\s*[-*+]\s+/.test(l);
const isOl = (l: string) => /^\s*\d+[.)]\s+/.test(l);
const isHead = (l: string) => /^#{1,6}\s/.test(l.trim());
const isRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
const cells = (r: string) => r.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());

export function renderMarkdown(md: string): ReactNode[] {
  const lines = md.split("\n");
  const out: ReactNode[] = [];
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }

    const h = /^(#{1,6})\s+(.*)$/.exec(line.trim());
    if (h) {
      const lvl = Math.min(h[1].length, 4);
      out.push(<div key={key} className={`md-h md-h${lvl}`}>{inline(h[2], `h${key++}`)}</div>);
      i++; continue;
    }
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) { out.push(<hr key={key++} className="md-hr"/>); i++; continue; }

    // Таблица: строка-заголовок + строка-разделитель |---|---|
    if (isRow(line) && i + 1 < lines.length && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      const head = cells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isRow(lines[i])) { rows.push(cells(lines[i])); i++; }
      out.push(
        <div key={key++} className="md-table-wrap">
          <table className="md-table">
            <thead><tr>{head.map((c, j) => <th key={j}>{inline(c, `th${j}`)}</th>)}</tr></thead>
            <tbody>{rows.map((r, ri) => (
              <tr key={ri}>{r.map((c, ci) => <td key={ci}>{inline(c, `td${ri}-${ci}`)}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (isUl(line) || isOl(line)) {
      const ordered = isOl(line);
      const items: string[] = [];
      while (i < lines.length && (ordered ? isOl(lines[i]) : isUl(lines[i]))) {
        items.push(lines[i].replace(ordered ? /^\s*\d+[.)]\s+/ : /^\s*[-*+]\s+/, ""));
        i++;
      }
      const kids = items.map((it, j) => <li key={j}>{inline(it, `li${key}-${j}`)}</li>);
      out.push(ordered
        ? <ol key={key++} className="md-list">{kids}</ol>
        : <ul key={key++} className="md-list">{kids}</ul>);
      continue;
    }

    // Абзац: склеиваем подряд идущие обычные строки
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !isHead(lines[i])
           && !isUl(lines[i]) && !isOl(lines[i]) && !isRow(lines[i])) {
      para.push(lines[i].trim()); i++;
    }
    out.push(<p key={key} className="md-p">{inline(para.join(" "), `p${key++}`)}</p>);
  }
  return out;
}

/** Markdown + штатное сворачивание «Показать больше» (как у длинных сообщений). */
export function CollapsibleMarkdown({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  const rendered = useMemo(() => renderMarkdown(text), [text]);
  if (text.length <= LONG_MD_THRESHOLD) return <div className="chat-md">{rendered}</div>;
  return (
    <>
      <div className={`chat-md chat-bubble-collapsible${expanded ? "" : " is-collapsed"}`}>
        {rendered}
      </div>
      <button
        type="button"
        className="chat-bubble-toggle"
        onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
      >
        {expanded ? "Свернуть" : "Показать больше"}
      </button>
    </>
  );
}
